/**
 * useCustomFields — Hook for loading field definitions + custom field values
 * for a system module (e.g. "workers").
 *
 * Usage:
 *   const { fields, sections, values, setValues, loading, saveValues } = useCustomFields('workers', recordId);
 */
import { useState, useEffect, useCallback } from 'react';
import configService from '../services/configService';

export default function useCustomFields(moduleKey, recordId) {
  const [fields, setFields] = useState([]);
  const [sections, setSections] = useState([]);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Load field definitions for the module
  useEffect(() => {
    if (!moduleKey) { setLoading(false); return; }
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const r = await configService.getModuleByKey(moduleKey);
        if (!cancelled && r.success) {
          setFields(r.data.fields || []);
          setSections(r.data.sections || []);
        }
      } catch (e) {
        if (!cancelled) setError(e.message);
      }
      if (!cancelled) setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [moduleKey]);

  // Load saved values for this record
  useEffect(() => {
    if (!moduleKey || !recordId) return;
    let cancelled = false;

    (async () => {
      try {
        const r = await configService.getCustomFieldValues(moduleKey, recordId);
        if (!cancelled && r.success) {
          const valMap = {};
          (r.data || []).forEach(v => { valMap[v.field_key] = v.value; });
          setValues(valMap);
        }
      } catch (e) {
        if (!cancelled) console.error('Failed to load custom field values:', e);
      }
    })();

    return () => { cancelled = true; };
  }, [moduleKey, recordId]);

  // Save custom field values
  const saveValues = useCallback(async (overrideValues) => {
    if (!moduleKey || !recordId) return false;
    setSaving(true);
    try {
      const toSave = overrideValues || values;
      await configService.saveCustomFieldValues(moduleKey, recordId, toSave);
      setSaving(false);
      return true;
    } catch (e) {
      setError(e.message);
      setSaving(false);
      return false;
    }
  }, [moduleKey, recordId, values]);

  return { fields, sections, values, setValues, loading, saving, saveValues, error };
}
